// GalleryPage(APP#42)—— 本地相簿清單頁(截圖 / 錄影瀏覽器)。
//
// 路由:
//   /gallery              帳號層總覽(全部本機媒體)
//   /gallery/:deviceId    裝置層(只該裝置)
// 讀 IndexedDB(useGallery),依日期分組成網格縮圖;點縮圖開 MediaViewer(相片 / 影片
// 檢視,影片含倍速)。純本地、零後端 —— 不打任何 API。

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { formatSize, groupByDay, timeLabel, type GalleryMeta } from './galleryLib';
import { useGallery, useObjectUrl } from './useGallery';
import { MediaViewer } from './MediaViewer';
import './gallery.css';

/**
 * 縮圖進入視窗(含 200px 預載邊界)才回 true —— 用來延遲載入 blob。
 * 一旦進入即維持 true(sticky):避免捲動來回反覆撤銷/重建 object URL 造成閃爍。
 * 無 IntersectionObserver 的環境(jsdom / 老瀏覽器)直接回 true(退回原本行為)。
 */
function useInView<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (inView || !el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);
  return [ref, inView];
}

/**
 * 單一縮圖:延遲載入 —— 僅在捲入視窗後才依 id 取 blob 建 object URL(卸載自動撤銷)。
 * 避免相簿一開就把每張的完整位元組全載進記憶體(大相簿峰值過高,APP#42 review)。
 */
function GalleryTile({ item, onOpen }: { item: GalleryMeta; onOpen: () => void }) {
  const { t } = useTranslation();
  const [ref, inView] = useInView<HTMLButtonElement>();
  const { url } = useObjectUrl(inView ? item.id : null);
  const isVideo = item.kind === 'video';
  return (
    <button ref={ref} type="button" className="gal-tile" onClick={onOpen} data-testid="gal-tile" aria-label={item.filename}>
      {url ? (
        isVideo ? (
          // preload=metadata + #t=0.1 → 多數瀏覽器顯示首幀當縮圖。
          <video className="gal-tile-media" src={`${url}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1} />
        ) : (
          <img className="gal-tile-media" src={url} alt={item.filename} />
        )
      ) : (
        <span className="gal-tile-media" aria-hidden="true" />
      )}
      <span className="gal-tile-badge">{isVideo ? `▶ ${t('gallery.kindVideo')}` : t('gallery.kindPhoto')}</span>
      <span className="gal-tile-meta">
        {timeLabel(item.createdAt)}
        {item.size ? ` · ${formatSize(item.size)}` : ''}
      </span>
    </button>
  );
}

export default function GalleryPage() {
  const { t } = useTranslation();
  const params = useParams<{ deviceId?: string }>();
  const deviceId = params.deviceId;
  const { items, loading, available, remove } = useGallery(deviceId);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const groups = useMemo(() => groupByDay(items), [items]);
  // 檢視器導覽用的扁平序列(與網格同順序:由新到舊)。
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const scopeLabel = deviceId ? items[0]?.deviceName || deviceId : null;

  const openAt = (item: GalleryMeta) => {
    const i = flat.findIndex((it) => it.id === item.id);
    if (i >= 0) setViewerIndex(i);
  };

  const handleDelete = async (id: string) => {
    await remove(id);
    // 刪除後修正檢視器索引(維持在合法範圍;空了就關閉)。
    setViewerIndex((prev) => {
      if (prev == null) return null;
      const nextLen = flat.length - 1;
      if (nextLen <= 0) return null;
      return Math.min(prev, nextLen - 1);
    });
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div className="gal-toolbar">
        <Link className="btn-ghost" to={deviceId ? `/devices/${encodeURIComponent(deviceId)}` : '/account'}>
          ← {t('gallery.back')}
        </Link>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-2xl)' }}>{t('gallery.title')}</h1>
        {scopeLabel && <span className="gal-count">· {scopeLabel}</span>}
        {!loading && items.length > 0 && <span className="gal-count">{t('gallery.count', { count: items.length })}</span>}
      </div>

      {!available && <p className="gal-empty">{t('gallery.unsupported')}</p>}

      {available && loading && <p className="text-secondary">{t('common.loading')}</p>}

      {available && !loading && items.length === 0 && (
        <div className="gal-empty">
          <p>{t('gallery.empty')}</p>
          <p className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>{t('gallery.emptyHint')}</p>
        </div>
      )}

      {available &&
        !loading &&
        groups.map((g) => (
          <section key={g.day}>
            <h2 className="gal-day">{g.day}</h2>
            <div className="gal-grid">
              {g.items.map((item) => (
                <GalleryTile key={item.id} item={item} onOpen={() => openAt(item)} />
              ))}
            </div>
          </section>
        ))}

      {viewerIndex != null && flat[viewerIndex] && (
        <MediaViewer
          items={flat}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          onDelete={(id) => void handleDelete(id)}
        />
      )}
    </div>
  );
}
